import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, CheckCircle2, XCircle, Gift, Save, Plus, Trash2, Globe, ShieldCheck, Clock } from "lucide-react";
import { ExportMenu } from "@/components/ExportMenu";
import { TableSkeleton } from "@/components/admin/AdminSkeletons";

const statusBadge = (s) => {
  const cls = { trial: "bg-green-600", active: "bg-blue-500", suspended: "bg-red-500" }[s] || "bg-slate-400";
  return <Badge className={`${cls} capitalize`}>{s}</Badge>;
};
const CUR_SYMBOL = { EUR: "€", USD: "$", NGN: "₦", GBP: "£" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const planLabel = (m) => (m === 12 ? "1 year" : m ? `${m} months` : "—");

// ---------- Per-country subscription pricing editor ----------
const PricingEditor = () => {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [newRow, setNewRow] = useState({ country: "", currency: "EUR" });

  const load = useCallback(() => {
    api.get("/admin/subscription-pricing").then((r) => setData(r.data)).catch(() => toast.error("Failed to load pricing"));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <TableSkeleton rows={3} cols={6} />;
  const durations = data.durations;

  const draftFor = (row) => drafts[row.country] || { currency: row.currency, prices: { ...row.prices } };

  const setDraft = (country, patch) => setDrafts((d) => ({ ...d, [country]: { ...(d[country] || {}), ...patch } }));

  const save = async (row) => {
    const dr = draftFor(row);
    setBusy(row.country);
    try {
      await api.put("/admin/subscription-pricing", {
        country: row.country, currency: dr.currency,
        prices: Object.fromEntries(durations.map((d) => [String(d), parseFloat(dr.prices[String(d)]) || 0])),
        active: true,
      });
      toast.success(`${row.country} pricing saved`);
      setDrafts((d) => { const n = { ...d }; delete n[row.country]; return n; });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(null);
  };

  const remove = async (country) => {
    if (!window.confirm(`Delete pricing for ${country}?`)) return;
    setBusy(country);
    try { await api.delete(`/admin/subscription-pricing/${encodeURIComponent(country)}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(null);
  };

  const addCountry = async () => {
    if (!newRow.country.trim()) return toast.error("Enter a country name");
    setBusy("__new");
    try {
      await api.put("/admin/subscription-pricing", {
        country: newRow.country.trim(), currency: newRow.currency,
        prices: Object.fromEntries(durations.map((d) => [String(d), 0])), active: true,
      });
      toast.success(`${newRow.country.trim()} added`);
      setNewRow({ country: "", currency: "EUR" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(null);
  };

  return (
    <Card className="p-0 overflow-hidden shadow-sm">
      <div className="p-4 border-b bg-muted/30 flex items-center gap-2">
        <Globe className="w-4 h-4 text-primary" />
        <h3 className="font-semibold text-sm">Subscription pricing by country</h3>
        <span className="text-xs text-muted-foreground ml-auto">Set the price for each plan in each country's currency. "Default" is used for any country without its own row.</span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Country</TableHead>
              <TableHead>Currency</TableHead>
              {durations.map((d) => <TableHead key={d}>{data.labels[String(d)]}</TableHead>)}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((row) => {
              const dr = draftFor(row);
              return (
                <TableRow key={row.country} data-testid={`pricing-row-${row.country}`}>
                  <TableCell className="font-medium">{row.country}{row.country === "Default" && <span className="block text-[10px] text-muted-foreground">fallback</span>}</TableCell>
                  <TableCell>
                    <Input className="h-8 w-20 uppercase" data-testid={`pricing-currency-${row.country}`}
                      value={dr.currency} maxLength={3}
                      onChange={(e) => setDraft(row.country, { currency: e.target.value.toUpperCase(), prices: dr.prices })} />
                  </TableCell>
                  {durations.map((d) => (
                    <TableCell key={d}>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground">{CUR_SYMBOL[dr.currency] || ""}</span>
                        <Input type="number" step="0.01" className="h-8 w-24" data-testid={`pricing-${row.country}-${d}`}
                          value={dr.prices[String(d)] ?? ""}
                          onChange={(e) => setDraft(row.country, { currency: dr.currency, prices: { ...dr.prices, [String(d)]: e.target.value } })} />
                      </div>
                    </TableCell>
                  ))}
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" className="h-8" disabled={busy === row.country} data-testid={`pricing-save-${row.country}`} onClick={() => save(row)}>
                      {busy === row.country ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}
                    </Button>
                    {row.country !== "Default" && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" disabled={busy === row.country} data-testid={`pricing-delete-${row.country}`} onClick={() => remove(row.country)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="p-4 border-t flex flex-wrap items-end gap-2">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Add a country</label>
          <Input className="h-9 w-44" placeholder="e.g. Germany" data-testid="pricing-new-country" value={newRow.country} onChange={(e) => setNewRow((r) => ({ ...r, country: e.target.value }))} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Currency</label>
          <Input className="h-9 w-24 uppercase" maxLength={3} data-testid="pricing-new-currency" value={newRow.currency} onChange={(e) => setNewRow((r) => ({ ...r, currency: e.target.value.toUpperCase() }))} />
        </div>
        <Button className="h-9" data-testid="pricing-add-btn" disabled={busy === "__new"} onClick={addCountry}>
          {busy === "__new" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Add</>}
        </Button>
      </div>
    </Card>
  );
};

export const SubscriptionManager = () => {
  const [rows, setRows] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [autoApprove, setAutoApprove] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);
  const [graceDays, setGraceDays] = useState("");
  const [reminderDays, setReminderDays] = useState("");
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [runningExpiry, setRunningExpiry] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/subscriptions").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load subscriptions"));
    api.get("/admin/finance/settings").then((r) => {
      setAutoApprove(!!r.data.auto_approve_sellers);
      setGraceDays(String(r.data.subscription_grace_days ?? 7));
      setReminderDays(String(r.data.subscription_reminder_days ?? 7));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      await api.put("/admin/finance/settings", { subscription_grace_days: parseInt(graceDays) || 0, subscription_reminder_days: parseInt(reminderDays) || 0 });
      toast.success("Expiry policy saved");
    } catch { toast.error("Failed to save"); }
    setSavingPolicy(false);
  };

  const runExpiry = async () => {
    setRunningExpiry(true);
    try {
      const { data } = await api.post("/admin/subscription-expiry/run");
      toast.success(`Expiry check ran for ${data.checked} seller(s)`);
      load();
    } catch { toast.error("Failed to run"); }
    setRunningExpiry(false);
  };

  const toggleAutoApprove = async (val) => {
    setSavingToggle(true);
    setAutoApprove(val);
    try { await api.put("/admin/finance/settings", { auto_approve_sellers: val }); toast.success(val ? "New sellers auto-approved" : "New sellers require manual approval"); }
    catch { toast.error("Failed to update"); setAutoApprove(!val); }
    setSavingToggle(false);
  };

  const act = async (id, body, msg) => {
    setBusy(id);
    try { await api.post(`/admin/subscriptions/${id}`, body); toast.success(msg); load(); setDrafts((d) => { const n = { ...d }; delete n[id]; return n; }); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  if (!rows) return <TableSkeleton rows={6} cols={6} />;

  const cols = [
    { header: "Seller", accessor: (r) => r.business_name },
    { header: "Email", accessor: (r) => r.email },
    { header: "Country", accessor: (r) => r.country || "" },
    { header: "Plan", accessor: (r) => planLabel(r.plan_months) },
    { header: "Renews", accessor: (r) => fmtDate(r.subscription_expires_at) },
    { header: "Status", accessor: (r) => r.subscription_status },
    { header: "Commission %", accessor: (r) => r.commission_pct },
    { header: "Lifetime commission (EUR)", accessor: (r) => (r.lifetime_commission || 0).toFixed(2) },
  ];

  return (
    <div className="space-y-4">
      <Card className="p-4 flex items-center gap-3 bg-primary/5 border-primary/20">
        <Gift className="w-5 h-5 text-primary shrink-0" />
        <p className="text-sm text-muted-foreground">Sellers sell free during a <strong className="text-foreground">3-month trial</strong> (0% commission), then pay a subscription <strong className="text-foreground">plus a 5% commission</strong> on each sale. Set plan prices per country below.</p>
      </Card>

      {/* Auto vs manual seller approval */}
      <Card className="p-4 flex items-center gap-3">
        <ShieldCheck className="w-5 h-5 text-primary shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold">Automatic seller approval</p>
          <p className="text-xs text-muted-foreground">When ON, new seller applications are approved instantly. When OFF, you approve each one manually in Seller Approvals.</p>
        </div>
        {autoApprove === null ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">{autoApprove ? "Automatic" : "Manual"}</span>
            <Switch checked={autoApprove} disabled={savingToggle} onCheckedChange={toggleAutoApprove} data-testid="auto-approve-sellers-toggle" />
          </div>
        )}
      </Card>

      <PricingEditor />

      {/* Subscription expiry policy */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-5 h-5 text-primary shrink-0" />
          <div>
            <p className="text-sm font-semibold">Expiry & grace policy</p>
            <p className="text-xs text-muted-foreground">When a plan lapses, the store stays live for the grace period, then is gently paused (hidden from shoppers) until renewal. Sellers are reminded before expiry.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Remind before expiry (days)</label>
            <Input type="number" className="h-9 w-28" data-testid="reminder-days-input" value={reminderDays} onChange={(e) => setReminderDays(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Grace period after expiry (days)</label>
            <Input type="number" className="h-9 w-28" data-testid="grace-days-input" value={graceDays} onChange={(e) => setGraceDays(e.target.value)} />
          </div>
          <Button className="h-9" data-testid="save-expiry-policy-btn" disabled={savingPolicy} onClick={savePolicy}>
            {savingPolicy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save policy</>}
          </Button>
          <Button variant="outline" className="h-9" data-testid="run-expiry-btn" disabled={runningExpiry} onClick={runExpiry}>
            {runningExpiry ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run expiry check now"}
          </Button>
        </div>
      </Card>

      <div className="flex justify-end">
        <ExportMenu filename="subscriptions" title="Subscriptions" columns={cols} rows={rows} testid="subscriptions-export" />
      </div>

      <Card className="p-0 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Seller</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead className="hidden md:table-cell">Renews</TableHead>
              <TableHead className="hidden md:table-cell">Trial</TableHead>
              <TableHead>Commission</TableHead>
              <TableHead className="hidden lg:table-cell">Lifetime</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const draft = drafts[r.user_id];
              const rateVal = draft?.rate ?? (r.override_rate != null ? String(r.override_rate * 100) : String(r.commission_pct));
              return (
                <TableRow key={r.user_id} data-testid={`sub-row-${r.user_id}`}>
                  <TableCell>
                    <p className="font-medium text-sm">{r.business_name}</p>
                    <p className="text-[11px] text-muted-foreground">{r.email}</p>
                    {r.country && <p className="text-[11px] text-muted-foreground">{r.country}</p>}
                  </TableCell>
                  <TableCell>
                    {statusBadge(r.subscription_status)}
                    {r.plan_months ? <p className="text-[11px] text-muted-foreground mt-1">{planLabel(r.plan_months)}</p> : null}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{r.subscription_expires_at ? fmtDate(r.subscription_expires_at) : "—"}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{r.on_trial ? `${r.trial_days_left}d left` : "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Input type="number" step="0.1" className="h-8 w-20" data-testid={`sub-rate-${r.user_id}`}
                        value={rateVal} onChange={(e) => setDrafts((d) => ({ ...d, [r.user_id]: { ...d[r.user_id], rate: e.target.value } }))} />
                      <span className="text-xs text-muted-foreground">%</span>
                      <Button size="icon" variant="ghost" className="h-8 w-8" disabled={busy === r.user_id}
                        data-testid={`sub-save-rate-${r.user_id}`} onClick={() => act(r.user_id, { commission_rate: parseFloat(rateVal) }, "Commission updated")}>
                        <Save className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm font-medium text-green-600">€{(r.lifetime_commission || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap space-x-1">
                    <Button size="sm" variant="outline" className="h-8" disabled={busy === r.user_id || r.subscription_status === "active"}
                      data-testid={`sub-activate-${r.user_id}`} onClick={() => act(r.user_id, { subscription_status: "active" }, "Plan activated")}>
                      <CheckCircle2 className="w-4 h-4 mr-1 text-green-600" />Activate
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" disabled={busy === r.user_id || r.subscription_status === "trial"}
                      data-testid={`sub-trial-${r.user_id}`} onClick={() => act(r.user_id, { subscription_status: "trial" }, "Moved to trial")}>
                      Trial
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" disabled={busy === r.user_id}
                      data-testid={`sub-suspend-${r.user_id}`} onClick={() => act(r.user_id, { subscription_status: "suspended" }, "Plan suspended")}>
                      <XCircle className="w-4 h-4 mr-1" />Suspend
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground text-sm">No sellers yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
        </div>
      </Card>
    </div>
  );
};
